import { BancoAbortError, BancoApiError, BancoNetworkError } from "bancojs";
import { useNotificationsStore } from "@/stores/notifications";
import { useAuthStore } from "@/stores/auth";
import type { FormErrors } from "@/composables/useFormErrors";

/**
 * Traducción de errores del SDK a algo que el usuario entienda.
 *
 * Reemplaza los ~40 bloques `catch { console.log(err); $notify({...}) }`
 * escritos a mano por una sola llamada. Además de deduplicar, distingue por
 * status en vez de mostrar siempre el mismo texto genérico.
 */

export interface ReportContext {
  /** Título del snackbar, p. ej. "Llaves". */
  title: string;
  /** Texto para cuando el servidor no aporta uno. */
  fallback: string;
  /**
   * Si se pasa, los errores de un 422 se pintan sobre los campos del formulario
   * en vez de sólo mostrarse en el snackbar.
   */
  form?: FormErrors;
}

export function useApiErrors() {
  const notifications = useNotificationsStore();
  const auth = useAuthStore();

  function report(err: unknown, ctx: ReportContext): void {
    // Cancelar no es un error: pasa al desmontar un componente o al reescribir
    // una búsqueda con debounce.
    if (BancoAbortError.is(err) && !err.timedOut) return;

    if (BancoAbortError.is(err)) {
      notifications.error("La operación tardó demasiado y se canceló.", ctx.title);
      return;
    }

    if (BancoNetworkError.is(err)) {
      notifications.error("No fue posible conectar con el servidor.", ctx.title);
      return;
    }

    if (!BancoApiError.is(err)) {
      // Un error de programación, no de la API. Al log, no a la cara del usuario.
      console.error("[api] error inesperado", err);
      notifications.error(ctx.fallback, ctx.title);
      return;
    }

    switch (err.status) {
      case 401:
        // El interceptor del cliente ya intentó renovar y reintentar. Si sigue
        // en 401 la sesión está realmente perdida.
        auth.logout({
          post_logout_redirect_uri: `${window.location.origin}/?notify=error`,
        });
        return;

      case 403:
        notifications.warning(
          err.serverMessage || "No tiene permisos para realizar esta acción.",
          ctx.title
        );
        return;

      case 404:
        notifications.error(err.serverMessage || ctx.fallback, ctx.title);
        return;

      case 409:
      case 412:
        // Acá el mensaje del servidor es lo único que explica el motivo:
        // "Key alias=X already exists", "It is not allowed to delete a system
        // key". Requiere server.error.include-message=ALWAYS en la API.
        notifications.error(err.serverMessage || ctx.fallback, ctx.title);
        return;

      case 422: {
        const fields = err.fieldErrors();
        if (ctx.form && Object.keys(fields).length > 0) {
          ctx.form.set(fields);
          notifications.warning("Revise los campos marcados.", ctx.title);
        } else {
          // Sin formulario donde pintarlos, se muestran en el snackbar antes que
          // perderlos.
          const flat = err.validationErrors.map((e) => e.message).join(" · ");
          notifications.warning(flat || ctx.fallback, ctx.title);
        }
        return;
      }

      default:
        if (err.status >= 500) {
          // El único lugar donde el log a consola sigue siendo útil.
          console.error("[api] error del servidor", err);
        }
        notifications.error(err.serverMessage || ctx.fallback, ctx.title);
    }
  }

  return { report };
}
